/**
 * Conversation filter accordion module
 * Provides search, agent, and collection filtering for the conversation list.
 */

import { fetchAgents, fetchCollections } from './api.js';
import { escapeHtml } from './html-escape.js';

/** Filter state */
const filterState = { agent: null, collection: null, search: '', includeRemote: false, includeLoops: false };

/** Cached lists (lazy-loaded on first picker open) */
let agentsList = null;
let collectionsList = null;

/** DOM refs */
let accordion = null;
let toggleBtn = null;
let badgesContainer = null;
let searchInput = null;
let agentChip = null;
let collectionChip = null;
let listContainer = null;

/** Callback to invoke when any filter changes */
let onChangeCallback = null;

/** Debounce timer for search input */
let searchTimer = null;

/** Whether a filter-triggered fetch is in progress */
let filterLoading = false;
let filterLoadingStart = 0;
const MIN_SPINNER_MS = 400;

/**
 * Initialize the conversation filter system.
 * @param {Function} callback - called whenever filters change
 */
export function initConvFilters(callback) {
    onChangeCallback = callback;

    accordion = document.getElementById('conv-filter-accordion');
    toggleBtn = document.getElementById('conv-filter-toggle');
    badgesContainer = document.getElementById('conv-filter-badges');
    searchInput = document.getElementById('conv-filter-search');
    agentChip = document.getElementById('conv-filter-agent-btn');
    collectionChip = document.getElementById('conv-filter-collection-btn');
    listContainer = document.getElementById('conversations-list');

    if (!accordion || !toggleBtn) return;

    // Toggle expand/collapse
    toggleBtn.addEventListener('click', () => {
        accordion.classList.toggle('expanded');
        updateBadges();
    });

    // Search input with debounce (1.5s after user stops typing)
    if (searchInput) {
        searchInput.addEventListener('input', () => {
            clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                const newVal = searchInput.value.trim();
                if (newVal === filterState.search) return; // no change
                filterState.search = newVal;
                updateBadges();
                triggerFilterChange();
            }, 1500);
        });

        // Allow Enter key to search immediately
        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                clearTimeout(searchTimer);
                const newVal = searchInput.value.trim();
                if (newVal === filterState.search) return;
                filterState.search = newVal;
                updateBadges();
                triggerFilterChange();
            }
        });
    }

    // Agent chip
    if (agentChip) {
        agentChip.addEventListener('click', (e) => {
            if (e.target.closest('.chip-clear')) {
                clearAgent();
                return;
            }
            openAgentPicker();
        });
    }

    // Show-remote toggle. Off by default; flipping it on adds remote-source
    // conversations to the list. Container scoping (only-your-container) is
    // handled server-side via auth and is independent of this toggle.
    const remoteToggle = document.getElementById('conv-filter-include-remote');
    if (remoteToggle) {
        remoteToggle.checked = !!filterState.includeRemote;
        remoteToggle.addEventListener('change', () => {
            filterState.includeRemote = !!remoteToggle.checked;
            updateBadges();
            triggerFilterChange();
        });
    }

    // Show-loop-sessions toggle. Off by default: a loop writes one session
    // per loop, iteration after iteration of the same working prompt, and
    // it buried the user's own conversations at the top of the list.
    const loopsToggle = document.getElementById('conv-filter-include-loops');
    if (loopsToggle) {
        loopsToggle.checked = !!filterState.includeLoops;
        loopsToggle.addEventListener('change', () => {
            filterState.includeLoops = !!loopsToggle.checked;
            updateBadges();
            triggerFilterChange();
        });
    }

    // Collection chip
    if (collectionChip) {
        collectionChip.addEventListener('click', (e) => {
            if (e.target.closest('.chip-clear')) {
                clearCollection();
                return;
            }
            openCollectionPicker();
        });
    }

    // Create picker modals in the DOM
    createPickerModals();
}

/**
 * Returns URL query string params for active filters.
 *
 * Remote-source conversations (e.g. dictations captured by the firefly-ragsmith
 * WordPress notes plugin) are hidden by default so the primary list shows the
 * user's own chat history. The "Show remote" toggle in the filter accordion
 * flips `filterState.includeRemote` and re-fetches, which causes this builder
 * to drop the exclude clause so the server returns everything (still scoped by
 * container_token at the auth layer when the caller is inside a container).
 */
export function getFilterParams() {
    let params = '';
    if (filterState.agent) params += `&agent=${encodeURIComponent(filterState.agent)}`;
    if (filterState.collection) params += `&collection=${encodeURIComponent(filterState.collection)}`;
    if (filterState.search) params += `&search=${encodeURIComponent(filterState.search)}`;
    // Hide all WP remote-origin conversations by default. The list is
    // comma-separated and the server splits + NOT-INs them. Add new wp-*
    // sources here as remote features land (wp-agent shipped 2026-06-08).
    // 'delegation' (meta-agent sub-runs) is ALWAYS hidden — those working
    // sessions are internal; their content shows in the parent's step log.
    // 'loop' (a loop's working session) is hidden unless its toggle is on.
    const hidden = ['delegation'];
    if (!filterState.includeRemote) hidden.push('wp-notes', 'wp-agent');
    if (!filterState.includeLoops) hidden.push('loop');
    params += `&exclude_source=${hidden.join(',')}`;
    return params;
}

/**
 * Returns true if any filter is active.
 */
export function hasActiveFilters() {
    return !!(filterState.agent || filterState.collection || filterState.search || filterState.includeRemote || filterState.includeLoops);
}

// ── Filter change with loading spinner ──

function triggerFilterChange() {
    if (!onChangeCallback) return;
    showFilterLoading();
    onChangeCallback();
}

function showFilterLoading() {
    if (!listContainer) return;
    filterLoading = true;
    filterLoadingStart = Date.now();
    listContainer.innerHTML = `
        <div class="conversations-loading">
            <div class="spinner"></div>
            <span>Searching...</span>
        </div>
    `;
}

/**
 * Returns true if a filter-triggered fetch is in progress.
 */
export function isFilterLoading() {
    return filterLoading;
}

/**
 * Returns a promise that resolves after the minimum spinner display time.
 * Call this before rendering results to ensure the spinner is visible long enough.
 */
export async function waitForMinSpinner() {
    if (!filterLoading) return;
    const elapsed = Date.now() - filterLoadingStart;
    if (elapsed < MIN_SPINNER_MS) {
        await new Promise(r => setTimeout(r, MIN_SPINNER_MS - elapsed));
    }
    filterLoading = false;
}

// ── Picker modal ──

function createPickerModals() {
    const html = `
        <div class="conv-filter-picker-overlay" id="conv-filter-picker-overlay">
            <div class="conv-filter-picker" id="conv-filter-picker">
                <div class="conv-filter-picker-header">
                    <span class="conv-filter-picker-title" id="conv-filter-picker-title">Select</span>
                    <button class="conv-filter-picker-close" id="conv-filter-picker-close">&times;</button>
                </div>
                <div class="conv-filter-picker-search">
                    <input type="text" id="conv-filter-picker-search-input" placeholder="Search...">
                </div>
                <div class="conv-filter-picker-list" id="conv-filter-picker-list"></div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', html);

    // Close button
    document.getElementById('conv-filter-picker-close').addEventListener('click', closePicker);

    // Overlay click to close
    document.getElementById('conv-filter-picker-overlay').addEventListener('click', (e) => {
        if (e.target === e.currentTarget) closePicker();
    });

    // Search within picker
    document.getElementById('conv-filter-picker-search-input').addEventListener('input', (e) => {
        filterPickerList(e.target.value.trim().toLowerCase());
    });
}

/** Current picker mode: 'agent' or 'collection' */
let pickerMode = null;

async function openAgentPicker() {
    pickerMode = 'agent';
    document.getElementById('conv-filter-picker-title').textContent = 'Select Agent';
    document.getElementById('conv-filter-picker-search-input').value = '';

    // Lazy-fetch agents
    if (!agentsList) {
        try {
            const data = await fetchAgents();
            agentsList = data.agents || [];
        } catch (e) {
            console.error('[ConvFilters] Failed to fetch agents:', e);
            agentsList = [];
        }
    }

    renderPickerList(agentsList.map(a => a.name || a), filterState.agent);
    showPicker();
}

async function openCollectionPicker() {
    pickerMode = 'collection';
    document.getElementById('conv-filter-picker-title').textContent = 'Select Collection';
    document.getElementById('conv-filter-picker-search-input').value = '';

    // Lazy-fetch collections
    if (!collectionsList) {
        try {
            const data = await fetchCollections();
            collectionsList = (data.collections || []).map(c => ({
                name: c.name,
                title: c.title || c.name
            }));
        } catch (e) {
            console.error('[ConvFilters] Failed to fetch collections:', e);
            collectionsList = [];
        }
    }

    const items = collectionsList.map(c => c.name);
    renderPickerList(items, filterState.collection);
    showPicker();
}

function renderPickerList(items, selectedValue) {
    const list = document.getElementById('conv-filter-picker-list');
    list.innerHTML = '';

    // "All" option
    const allItem = document.createElement('div');
    allItem.className = 'conv-filter-picker-item all-option';
    if (!selectedValue) allItem.classList.add('selected');
    allItem.textContent = pickerMode === 'agent' ? 'All Agents' : 'All Collections';
    allItem.addEventListener('click', () => {
        if (pickerMode === 'agent') {
            selectAgent(null);
        } else {
            selectCollection(null);
        }
    });
    list.appendChild(allItem);

    // Individual items
    items.forEach(name => {
        const el = document.createElement('div');
        el.className = 'conv-filter-picker-item';
        el.dataset.name = name;
        if (name === selectedValue) el.classList.add('selected');

        // For collections, show title if available
        if (pickerMode === 'collection') {
            const coll = collectionsList?.find(c => c.name === name);
            el.textContent = coll?.title || name;
        } else {
            el.textContent = name;
        }

        el.addEventListener('click', () => {
            if (pickerMode === 'agent') {
                selectAgent(name);
            } else {
                selectCollection(name);
            }
        });
        list.appendChild(el);
    });
}

function filterPickerList(query) {
    const list = document.getElementById('conv-filter-picker-list');
    const items = list.querySelectorAll('.conv-filter-picker-item');
    items.forEach(item => {
        if (item.classList.contains('all-option')) {
            item.style.display = query ? 'none' : '';
            return;
        }
        const text = item.textContent.toLowerCase();
        const name = (item.dataset.name || '').toLowerCase();
        item.style.display = (text.includes(query) || name.includes(query)) ? '' : 'none';
    });
}

function showPicker() {
    const overlay = document.getElementById('conv-filter-picker-overlay');
    const picker = document.getElementById('conv-filter-picker');
    overlay.classList.add('active');
    picker.classList.add('open');
    // Focus search
    document.getElementById('conv-filter-picker-search-input').focus();
}

function closePicker() {
    const overlay = document.getElementById('conv-filter-picker-overlay');
    const picker = document.getElementById('conv-filter-picker');
    overlay.classList.remove('active');
    picker.classList.remove('open');
    pickerMode = null;
}

function selectAgent(name) {
    filterState.agent = name;
    updateChip(agentChip, name, 'All Agents', 'active');
    closePicker();
    updateBadges();
    triggerFilterChange();
}

function selectCollection(name) {
    filterState.collection = name;
    // Show collection title if available
    let label = name;
    if (name && collectionsList) {
        const coll = collectionsList.find(c => c.name === name);
        if (coll?.title) label = coll.title;
    }
    updateChip(collectionChip, label, 'All Collections', 'collection-active');
    closePicker();
    updateBadges();
    triggerFilterChange();
}

function clearAgent() {
    filterState.agent = null;
    updateChip(agentChip, null, 'All Agents', 'active');
    updateBadges();
    triggerFilterChange();
}

function clearCollection() {
    filterState.collection = null;
    updateChip(collectionChip, null, 'All Collections', 'collection-active');
    updateBadges();
    triggerFilterChange();
}

function updateChip(chip, value, defaultLabel, activeClass) {
    if (!chip) return;
    const label = chip.querySelector('.chip-label');
    const clear = chip.querySelector('.chip-clear');

    if (value) {
        label.textContent = value;
        chip.classList.add(activeClass);
        clear.classList.remove('hidden');
    } else {
        label.textContent = defaultLabel;
        chip.classList.remove(activeClass);
        clear.classList.add('hidden');
    }
}

function updateBadges() {
    if (!badgesContainer) return;

    const isExpanded = accordion?.classList.contains('expanded');

    // Only show badges when collapsed and filters are active
    if (isExpanded || !hasActiveFilters()) {
        badgesContainer.innerHTML = '';
        return;
    }

    let html = '';
    if (filterState.agent) {
        html += `<span class="conv-filter-badge">${escapeHtml(filterState.agent)}</span>`;
    }
    if (filterState.collection) {
        let label = filterState.collection;
        if (collectionsList) {
            const coll = collectionsList.find(c => c.name === filterState.collection);
            if (coll?.title) label = coll.title;
        }
        html += `<span class="conv-filter-badge collection-badge">${escapeHtml(label)}</span>`;
    }
    if (filterState.search) {
        html += `<span class="conv-filter-badge search-badge">"${escapeHtml(filterState.search)}"</span>`;
    }
    if (filterState.includeRemote) {
        html += `<span class="conv-filter-badge remote-badge">+ remote</span>`;
    }
    if (filterState.includeLoops) {
        html += `<span class="conv-filter-badge remote-badge">+ loops</span>`;
    }
    badgesContainer.innerHTML = html;
}
